import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Params } from '@angular/router';
import { NextLocator, SelectOption } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { format } from 'date-fns';
import { catchError, firstValueFrom, Subject, takeUntil, throwError } from 'rxjs';
import { DTypeMigrationDestinyItem, DTypeMigrationItem, FlushMasterFormHierarchyBody, ImportDTypeMigrationItemBody } from '../dtype.types';
import { UserNodePrivileges } from '../../../modules/admin/projects/projects.types';
import { DTypeService } from '../dtype.service';
import { FuseConfirmationService } from '../../../../@fuse/services/confirmation/confirmation.service';
import { DialogsService } from '../../../dialogs/dialogs.service';
import { LocalizePipe } from '../../pipes/localize.pipe';
import { ProjectsService } from '../../../modules/admin/projects/projects.service';

@Component({
    selector: 'dtype-migration',
    templateUrl: './dtype-migration.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DTypeMigrationComponent implements OnInit, OnDestroy {

    migrationItems: DTypeMigrationItem[] = [];
    selection = new SelectionModel<DTypeMigrationItem>(true, []);
    destinyItems: DTypeMigrationDestinyItem[] = [];
    destinySelection = new SelectionModel<DTypeMigrationDestinyItem>(false, []);
    locator: string;
    captionLocator: string;
    facade: string;
    params: Params;

    role: UserNodePrivileges;

    destinationView: boolean = false;

    loading = true;

    typologies: SelectOption[];

    private unsubscribeAll = new Subject<void>();

    constructor(
        private dtypeService: DTypeService,
        private _fuseConfirmationService: FuseConfirmationService,
        private route: ActivatedRoute,
        private snackBar: MatSnackBar,
        private transloco: TranslocoService,
        private dialogsService: DialogsService,
        private localize: LocalizePipe,
        private projectsService: ProjectsService,
    ) { }

    ngOnInit() {
        this.route.queryParams
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(async (params) => {
                const [locator, stringLocator] = this.projectsService.buildLocatorFromParams(params);
                this.params = params;

                this.migrationItems = await firstValueFrom(this.dtypeService.retrieveDTypeHierarchy(locator));
                this.destinyItems = await firstValueFrom(this.dtypeService.retrieveDTypeDestiny(locator));

                this.role = await this.projectsService.getRoleByLocators(params);

                this.loading = false;
            });
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    getTypeCaption(item: DTypeMigrationItem) {
        return item.type === 'Master' ? this.transloco.translate('services.migration.master') : this.transloco.translate('services.migration.form');
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.migrationItems.length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    toggleAllRows() {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }

        this.selection.select(...this.migrationItems);
    }

    export() {
        const dialogRef = this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: this.transloco.translate('services.dialog.confirm.exportDType'),
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                const date = (format(new Date(), 'yyyy-MM-dd_HH-mm-ss'));
                const filenameFront = 'masterForm_d_' + date;
                this.dtypeService.exportDTypeHierarchy(this.selection.selected).pipe(
                    catchError((error) => {
                        console.error(error);
                        this.dialogsService.openErrorDialog({
                            message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                        });
                        return throwError(() => error);
                    })).subscribe((data) => {
                        const filename = String(data?.headers?.get('content-disposition')).replace('attachment; filename="', '').replace('"', '');
                        const element = document.createElement('a');
                        element.href = window.URL.createObjectURL(data.body);
                        element.download = filename ?? filenameFront;
                        element.click();
                    });
            }
        });
    }

    import($event) {
        const file = $event?.target?.files[0] ?? undefined;
        const [locator, stringLocator] = this.projectsService.buildLocatorFromParams(this.params);
        $event.stopPropagation();
        let base64: string;
        if (file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                base64 = String(reader.result);
                const body: ImportDTypeMigrationItemBody = {
                    locator: locator,
                    base64: base64
                };
                this.dtypeService.importDTypeHierarchy(body).pipe(
                    catchError((error) => {
                        console.error(error);
                        this.dialogsService.openErrorDialog({
                            message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                        });
                        return throwError(() => error);
                    })
                ).subscribe((reply) => {
                    if (reply && reply?.data) {
                        this.migrationItems = reply.data;
                    }
                    if (reply && reply.status === 200 && !reply.masterError) {
                        this.snackBar.open(this.transloco.translate('migrateMasterFormCompleted'),
                                    this.transloco.translate('accept'), { duration: 5000 });
                    }
                    if (reply && reply.masterError) {
                        this.dialogsService.openErrorDialog({
                            message: this.transloco.translate('services.dialog.confirm.notAllImported'),
                        });
                    }
                });
            };
        };
    }

    async migrate() {
        if (await this.confirm()) {
            const [locator, stringLocator] = this.projectsService.buildLocatorFromParams(this.params);
            const _locator: NextLocator = {
                OU: locator.OU,
                marca_code: this.destinySelection.selected[0].marca_code,
                cliente_code: this.destinySelection.selected[0].cliente_code ? Number(this.destinySelection.selected[0].cliente_code) : undefined,
                projectId: this.destinySelection.selected[0].projectId ?? undefined,
                serviceId: this.destinySelection.selected[0].serviceId ?? undefined
            };
            const body: FlushMasterFormHierarchyBody = {
                locator: _locator,
                items: this.selection.selected,
            };
            const reply = await firstValueFrom(this.dtypeService.flushDTypeHierarchy(body));
            if (reply && reply.status === 200 && !reply.masterError) {
                this.snackBar.open(this.transloco.translate('migrateMasterFormCompleted'),
                            this.transloco.translate('accept'), { duration: 5000 });
            }
            if (reply && reply.masterError) {
                this.dialogsService.openErrorDialog({
                    message: this.transloco.translate('services.dialog.confirm.notAllImported'),
                });
            }
        }
    }

    private confirm(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.transloco.translate('services.migration.migrate'),
            message: message ? message : this.transloco.translate('services.migration.migrateConfirm'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.transloco.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe(result => resolve(result === 'confirmed'));
        });
    }
}
